import {
  StyleSheet,
  Text,
  View,
  ScrollView,
  FlatList,
  Image,
  TextInput,
} from "react-native";
import React, { useEffect, useMemo, useState } from "react";
import { StackActions, useNavigation } from "@react-navigation/native";
import { navigateToScreen } from "../../../utility/NavigationHelper";
import {
  useDeviceSize,
  CreateResponsiveStyle,
  DEVICE_SIZES,
} from "rn-responsive-styles";
import { AppColors } from "../../../utility/AppColors";
import { AppImages } from "../../../utility/AppImages";
import { AppFontStyle } from "../../../styles/AppFontStyle";
import { format } from "date-fns";
import { CommonButton } from "../../../common/CommonButton";
import { TouchableOpacity } from "react-native-gesture-handler";
import { toast } from "react-toastify";
import GameShowSignUpModal from "./Modals/GameShowSignUpModal";
import { AppScreens } from "../../Utility/AppScreens";
import { getEventForNotLoginUser } from "../../../services/eventsAPI";
import { ShowLoader } from "../../../common/ShowLoader";
import ImageComponent from "../../../Components/ImageComponent";
import NoDataFound from "../../../Components/NoDataFound";
import GameShowEventDetailSkeleton from "../../../Components/skeleton-components/GameShowEventDetailSkeleton";
import PlayerViewSkeleton from "../../../Components/skeleton-components/PlayerViewSkeleton";
import Skeletons from "../../../Components/skeleton-components/Skeletons";
import EventDetailScreenCard from "./OnGoingEvents/EventDetailScreenCard";
import { EVENT_STATUS } from "../../../utility/LiveStreamSocket/LiveStreamConfig";
import Avatar from "../../../Components/choose-avatar-components/Avatar";
import { ScreenBGGradientView } from "../../../common/CommonView";
import StaticEventChatPreview from "./Common/StaticEventChatPreview";

export default function GameShowPreviewScreen({ route }) {
  const { eventID, invite } = route.params;
  const navigation = useNavigation();
  const styles = useStyles();
  const deviceSize = useDeviceSize();
  const [openSignUp, setOpenSignUp] = useState(false); // Only open for non-logged-in users
  const [details, setDetails] = useState(null);
  const [usersData, setUsersData] = useState([]);
  const [userData, setUserData] = useState(null); // Mock user data for preview
  const [showLoader, setShowLoader] = useState(true);
  const [referralCode, setReferralCode] = useState("");
  const [directReferrerId, setDirectReferrerId] = useState("");
  const [originalURL, setOriginalURL] = useState("");
  const [isMinAttendees, setIsMinAttendees] = useState(false);
  const [isMoreTimeLeft, setIsMoreTimeLeft] = useState(false);
  const [eventTooltipVisible, setEventTooltipVisible] = useState(false);
  const [fetchChatHistoryTrigger, setFetchChatHistoryTrigger] = useState(false);
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  // Check if user is logged in - only show signup modal for non-logged-in users
  useEffect(() => {
    const checkLoginStatus = async () => {
      try {
        const { getLoginData } = await import("../../../utility/AppStoreData");
        const loginData = await getLoginData();
        const isLoggedIn = loginData !== null;

        console.log("🎮 GameShowPreviewScreen: User logged in?", isLoggedIn);

        if (!isLoggedIn) {
          // Only open signup modal for non-logged-in users
          setOpenSignUp(true);
        } else {
          // User is logged in, redirect to actual GameShow screen
          console.log(
            "🔄 GameShowPreviewScreen: User is logged in, redirecting to GameShowScreen"
          );
          const redirectURL = `/events/game-show?eventID=${eventID}${
            invite ? `&invite=${invite}` : ""
          }`;
          window.location.href = redirectURL;
        }
      } catch (error) {
        console.error("Error checking login status:", error);
        // Default to showing signup modal if error
        setOpenSignUp(true);
      }
    };

    checkLoginStatus();
  }, []);

  useEffect(() => {
    console.log(
      "🎮 GameShowPreviewScreen: eventID:",
      eventID,
      "invite:",
      invite
    );

    if (eventID) {
      getEventDetails(eventID);
    }

    if (invite) {
      try {
        const decodedInvite = JSON.parse(atob(invite));
        if (decodedInvite.referralCode) {
          setReferralCode(decodedInvite.referralCode);
        }
        if (decodedInvite.directReferrerId) {
          setDirectReferrerId(decodedInvite.directReferrerId);
        }
      } catch (error) {
        console.error("Error parsing invite parameter:", error);
      }
    }

    const currentURL = `/events/game-show?eventID=${eventID}`;
    setOriginalURL(currentURL);
  }, [eventID, invite]);

  const getEventDetails = async (eventID) => {
    setShowLoader(true);

    try {
      const response = await getEventForNotLoginUser(eventID);

      if (response && response.event) {
        const eventData = response.event;
        setDetails(eventData);

        const attendeeIDs = eventData.attendeeIDs || [];
        const newUsersData = attendeeIDs.map(
          (username: String, index: any) => ({
            id: index + 1,
            username: username,
            userImage: AppImages.icApple,
          })
        );

        setUsersData(newUsersData);
      } else {
        handleErrorResponse(404, "Event not found");
      }
    } catch (error) {
      toast.error("Error fetching event details");
      // navigateToScreen(navigation, AppScreens.Home);
    } finally {
      setShowLoader(false);
    }
  };

  const handleErrorResponse = (statusCode, errorMessage) => {
    if (statusCode === 400) {
      toast.error("Bad Request");
    } else if (statusCode > 400 && statusCode < 420) {
      toast.error("Event not found or access denied");
    } else if (statusCode >= 500) {
      toast.error("Internal Server Error");
    } else {
      toast.error(errorMessage);
    }
    navigateToScreen(navigation, AppScreens.Home);
  };

  useEffect(() => {
    let hasNavigated = false;
    const calculateTimeLeft = () => {
      if (details && details.eventStartTime) {
        const eventDate = new Date(details.eventStartTime).getTime();
        const now = Date.now();
        const difference = eventDate - now;
        if (difference > 60 * 60 * 1000) {
          setTimeLeft({
            days: Math.floor(difference / (1000 * 60 * 60 * 24)),
            hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
            minutes: Math.floor((difference / 1000 / 60) % 60),
            seconds: Math.floor((difference / 1000) % 60),
          });
        } else {
          setTimeLeft({
            days: 0,
            hours: 0,
            minutes: 0,
            seconds: 0,
          });
        }
      }
    };

    const currentDate = new Date();
    const signUpNumber = 70;
    const requiredNumber = details?.minimumAttendees
      ? details.minimumAttendees - signUpNumber
      : 0;

    const startDate = new Date(details?.eventStartTime);
    const timeDifference = startDate.getTime() - currentDate.getTime();
    const hoursLeft = timeDifference / (1000 * 60 * 60);
    setIsMinAttendees(requiredNumber > signUpNumber ? true : false);
    if (hoursLeft <= 24 && hoursLeft >= 0) {
      setIsMoreTimeLeft(false);
    } else {
      setIsMoreTimeLeft(true);
    }

    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(interval);
  }, [details]);

  const date = useMemo(
    () => (details ? new Date(details?.eventStartTime) : new Date()),
    [details]
  );

  const formattedDate = details
    ? format(date, "EEEE, MMMM d, yyyy, 'at' h:mm")
    : "";

  // Mock user data for preview (non-logged in state)
  const mockUserData = { username: null };

  // Mock check-in status
  const isUserCheckedIn = false;

  // Merged details for attendees/participants
  const mergedDetails = [
    ...(details?.attendeeDetails || []),
    ...(details?.participantDetails || []),
  ].reduce((unique, item) => {
    if (!unique.some((user) => user.userID === item.userID)) {
      unique.push(item);
    }
    return unique;
  }, []);

  const isStreamConnected = "true";
  const IsEventCanceled = details?.status === EVENT_STATUS.CANCEL;

  return (
    <View
      style={{
        flex: 1,
      }}
    >
      <ScreenBGGradientView />

      <ScrollView
        style={styles.container}
        showsHorizontalScrollIndicator={false}
      >
        <View style={styles.headerSection}>
          <View style={styles.titleContainer}>
            <Text
              style={[
                AppFontStyle.BOLD_ANYBODY_28,
                { color: AppColors.white, textAlign: "center" },
              ]}
            >
              GameShow Event Preview
            </Text>
            <Text
              style={[
                AppFontStyle.REGULAR_ANYBODY_16,
                {
                  color: AppColors.gray_C3C3C3,
                  textAlign: "center",
                  marginTop: 5,
                },
              ]}
            >
              Sign up to join this exciting event!
            </Text>
          </View>
        </View>
        <EventDetailScreenCard
          setIsMinAttendees={setIsMinAttendees}
          isMinAttendees={isMinAttendees}
          details={details}
          userData={mockUserData}
          timeLeft={timeLeft}
          hasCheckedIn={isUserCheckedIn}
          setTimeLeft={setTimeLeft}
          isStreamConnected={isStreamConnected}
          isMoreTimeLeft={isMoreTimeLeft}
          setIsMoreTimeLeft={setIsMoreTimeLeft}
          screenName="GameShowPreviewScreen"
          IsEventCanceled={IsEventCanceled}
          isLoading={showLoader}
        />

        {/* Same free event view structure as GameShowScreen */}
        {showLoader ? (
          <GameShowEventDetailSkeleton />
        ) : IsEventCanceled ? (
          <View
            style={[
              styles.freeEventView,
              {
                marginTop: 15,
                justifyContent: "space-between",
                alignItems: "center",
                ...(deviceSize === DEVICE_SIZES.XS ? { gap: 10 } : {}),
              },
            ]}
          >
            <View
              style={{
                gap: 10,
                width: deviceSize === DEVICE_SIZES.XS ? "100%" : 478,
              }}
            >
              <Text
                style={[
                  AppFontStyle.BOLD_ANYBODY_12,
                  { color: AppColors.purple_C68DF6, textAlign: "center" },
                ]}
              >
                We apologise for the inconvenience and thank you for your
                understanding. We hope to see you at future events.
              </Text>
              <Text
                style={[
                  AppFontStyle.BOLD_ANYBODY_12,
                  { color: AppColors.purple_C68DF6, textAlign: "center" },
                ]}
              >
                The Kallout Team
              </Text>
            </View>
            <CommonButton
              title="Event Canceled"
              onTap={() => {
                setOpenSignUp(true);
              }}
              buttonStyle={{
                paddingVertical: 12,
                paddingHorizontal: 24,
                backgroundColor: AppColors.red_912121,
                shadowOpacity: 0,
                width: 212,
                borderRadius: 10,
              }}
              textStyle={[AppFontStyle.BOLD_ANYBODY_12, {}]}
            />
          </View>
        ) : (
          <View
            style={[
              styles.freeEventView,
              { marginTop: 15, justifyContent: "space-between" },
            ]}
          >
            {deviceSize === DEVICE_SIZES.XS ? (
              <View
                style={[
                  styles.deviceflexDirection,
                  {
                    height: "100%",
                    gap: 10,
                    borderRadius: 12,
                    backgroundColor: AppColors.grayColor_282828,
                    padding: 20,
                  },
                ]}
              >
                <View
                  style={{
                    borderRadius: 5,
                    alignSelf: "center",
                    paddingVertical: 5,
                  }}
                >
                  <View style={{ flexDirection: "row", columnGap: 10 }}>
                    <Text
                      style={[
                        AppFontStyle.REGULAR_ANYBODY_20,
                        { color: AppColors.white, flex: 1 },
                      ]}
                    >
                      {details?.eventType === "Public"
                        ? "FREE EVENT"
                        : `Available tickets ${
                            details?.ticketPrice ? details?.ticketPrice : 0
                          } `}
                    </Text>
                  </View>
                </View>

                <View
                  style={{
                    borderRadius: 5,
                    paddingHorizontal: 5,
                    width: "100%",
                  }}
                >
                  <View style={{ flexDirection: "row", columnGap: 10 }}>
                    <Text
                      style={[
                        AppFontStyle.REGULAR_ANYBODY_18,
                        { color: AppColors.white, flex: 1 },
                      ]}
                    >
                      {details?.eventType === "Public"
                        ? "Required Attendees"
                        : "Tickets sold"}
                    </Text>
                    <Text
                      style={[
                        AppFontStyle.BOLD_ANYBODY_24,
                        { color: AppColors.white },
                      ]}
                    >
                      {details ? details.minimumAttendees : ""}
                    </Text>
                  </View>
                </View>
                <View
                  style={{
                    borderRadius: 5,
                    paddingVertical: 5,
                    paddingHorizontal: 5,
                  }}
                >
                  <View style={{ flexDirection: "row", columnGap: 10 }}>
                    <Text
                      style={[
                        AppFontStyle.REGULAR_ANYBODY_18,
                        { color: AppColors.white, flex: 1 },
                      ]}
                    >
                      {details?.eventType === "Public"
                        ? "Signed-Up"
                        : "Tickets Price"}
                    </Text>
                    <Text
                      style={[
                        AppFontStyle.BOLD_ANYBODY_24,
                        { color: AppColors.white },
                      ]}
                    >
                      {details?.eventType === "Public"
                        ? details?.attendeeIDs?.length || 0
                        : details?.ticketPrice}
                    </Text>
                  </View>
                </View>
                <View
                  style={{
                    flexDirection: "row",
                    columnGap: 15,
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <TouchableOpacity
                    onPress={() => {
                      toast.info("Please sign up to share this event");
                    }}
                    style={{}}
                  >
                    <Image
                      source={AppImages.icshare}
                      style={{ height: 50, width: 50, cursor: "pointer" }}
                    />
                  </TouchableOpacity>

                  {timeLeft.days === 0 &&
                  timeLeft.hours === 0 &&
                  timeLeft.minutes === 0 &&
                  timeLeft.seconds === 0 ? (
                    <CommonButton
                      title="Sign Up to Join Event"
                      onTap={() => {
                        setOpenSignUp(true);
                      }}
                      buttonStyle={styles.btnButTicket}
                      textStyle={[AppFontStyle.REGULAR_ANYBODY_12, {}]}
                    />
                  ) : (
                    <CommonButton
                      title={
                        details?.eventType === "Public"
                          ? "Sign-Up to Watch Event"
                          : "Buy Ticket & Sign Up"
                      }
                      onTap={() => {
                        setOpenSignUp(true);
                      }}
                      buttonStyle={styles.btnSignUpStyle}
                      textStyle={[AppFontStyle.REGULAR_ANYBODY_12, {}]}
                    />
                  )}
                  <CommonButton
                    title="Event Chat"
                    onTap={() => {
                      toast.info("Please sign up to access event chat");
                    }}
                    buttonStyle={styles.btnEventChatStyle}
                    textStyle={styles.btnEventChatText}
                    image={true}
                    imageUrl={AppImages.KO_GAMESCREEN_CHAT_Icon}
                    imagePosition="pre"
                    imageStyle={{ width: 20, height: 20 }}
                  />
                </View>
              </View>
            ) : (
              <>
                <View
                  style={{
                    gap: 10,
                    flexDirection: "row",
                    borderRadius: 12,
                  }}
                >
                  {details?.eventType === "Public" && (
                    <View
                      style={{
                        backgroundColor: AppColors.grayColor_282828,
                        borderRadius: 5,
                        alignSelf: "center",
                        paddingVertical: 10,
                        paddingHorizontal: 5,
                      }}
                    >
                      <View
                        style={{
                          flexDirection: "row",
                          columnGap: 10,
                          alignItems: "center",
                        }}
                      >
                        <Text
                          style={[
                            deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE
                              ? AppFontStyle.REGULAR_ANYBODY_24
                              : AppFontStyle.REGULAR_ANYBODY_12,
                            { color: AppColors.white },
                          ]}
                        >
                          {details?.eventType === "Public"
                            ? "FREE EVENT"
                            : null}
                        </Text>
                      </View>
                    </View>
                  )}
                  {details?.eventType === "Public" ? null : (
                    <View
                      style={{
                        backgroundColor: AppColors.grayColor_282828,
                        borderRadius: 5,
                        alignSelf: "center",
                        paddingVertical: 10,
                        paddingHorizontal: 10,
                      }}
                    >
                      <View
                        style={{
                          flexDirection: "row",
                          columnGap: 10,
                          alignItems: "center",
                        }}
                      >
                        <Text
                          style={[
                            deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE
                              ? AppFontStyle.REGULAR_ANYBODY_24
                              : AppFontStyle.REGULAR_ANYBODY_12,
                            { color: AppColors.white },
                          ]}
                        >
                          Tickets sold
                        </Text>
                        <Text
                          style={[
                            deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE
                              ? AppFontStyle.BOLD_ANYBODY_32
                              : AppFontStyle.BOLD_ANYBODY_14,
                            { color: AppColors.white },
                          ]}
                        >
                          {details ? details.minimumAttendees : ""}
                        </Text>
                      </View>
                    </View>
                  )}
                  <View
                    style={{
                      backgroundColor: AppColors.grayColor_282828,
                      borderRadius: 5,
                      alignSelf: "center",
                      paddingVertical: 10,
                      paddingHorizontal: 10,
                    }}
                  >
                    <View
                      style={{
                        flexDirection: "row",
                        columnGap: 10,
                        alignItems: "center",
                      }}
                    >
                      <Text
                        style={[
                          deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE
                            ? AppFontStyle.REGULAR_ANYBODY_24
                            : AppFontStyle.REGULAR_ANYBODY_12,

                          { color: AppColors.white },
                        ]}
                      >
                        {details?.eventType === "Public"
                          ? "Signed-Up"
                          : "Tickets Price"}
                      </Text>
                      <Text
                        style={[
                          deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE
                            ? AppFontStyle.BOLD_ANYBODY_32
                            : AppFontStyle.BOLD_ANYBODY_14,
                          { color: AppColors.white },
                        ]}
                      >
                        {details?.eventType === "Public"
                          ? details?.attendeeIDs?.length || 0
                          : details?.ticketPrice}
                      </Text>
                      <Image
                        source={AppImages.icWonDollarIcon}
                        style={{ height: 23, width: 23, resizeMode: "contain" }}
                      />
                    </View>
                  </View>
                </View>
                <View
                  style={{
                    flexDirection: "row",
                    columnGap: deviceSize !== DEVICE_SIZES.SM ? 15 : 1,
                    alignSelf: "center",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <TouchableOpacity
                    onPress={() => {
                      toast.info("Please sign up to share this event");
                    }}
                    style={{}}
                  >
                    <Image
                      source={AppImages.icshare}
                      style={{ height: 50, width: 50, cursor: "pointer" }}
                    />
                  </TouchableOpacity>

                  {timeLeft.days === 0 &&
                  timeLeft.hours === 0 &&
                  timeLeft.minutes === 0 &&
                  timeLeft.seconds === 0 ? (
                    <CommonButton
                      title="Sign Up to Join Event"
                      onTap={() => {
                        setOpenSignUp(true);
                      }}
                      buttonStyle={styles.btnButTicket}
                      textStyle={[AppFontStyle.REGULAR_ANYBODY_12, {}]}
                    />
                  ) : (
                    <CommonButton
                      title={
                        details?.eventType === "Public"
                          ? "Sign-Up to Watch Event"
                          : "Buy Ticket & Sign Up"
                      }
                      onTap={() => {
                        setOpenSignUp(true);
                      }}
                      buttonStyle={styles.btnSignUpStyle}
                      textStyle={[AppFontStyle.REGULAR_ANYBODY_12, {}]}
                    />
                  )}
                </View>
              </>
            )}
          </View>
        )}

        <View
          style={{
            height: 1,
            backgroundColor: AppColors.gray_C3C3C3,
            marginTop: 20,
          }}
        />

        <View
          style={[
            {
              flexDirection:
                deviceSize === DEVICE_SIZES.XS ||
                deviceSize === DEVICE_SIZES.SM ||
                deviceSize === DEVICE_SIZES.MD ||
                deviceSize === DEVICE_SIZES.LG
                  ? "column"
                  : "row",
            },
          ]}
        >
          <View
            style={{
              marginTop: 20,
              flex:
                deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM
                  ? 1
                  : 0.7,
            }}
          >
            {showLoader ? (
              <PlayerViewSkeleton />
            ) : (
              <View style={styles.playersView}>
                <View>
                  <Text
                    style={[AppFontStyle.BOLD_ANYBODY_22, { marginTop: 10 }]}
                  >
                    Player 1
                  </Text>
                  <Text
                    style={[
                      AppFontStyle.BOLD_ANYBODY_12,
                      {
                        marginVertical: 7,
                        color: AppColors.purple_B155FF,
                      },
                    ]}
                  >
                    Kaller
                  </Text>
                  <TouchableOpacity
                    onPress={() => {
                      toast.info("Please sign up to view player profiles");
                    }}
                    style={styles.playerView}
                  >
                    <Avatar
                      variant="OnGoingPlayerDetail"
                      src={
                        details
                          ? details?.participantDetails?.[0]?.profilePicture
                          : "https://squadify-avatar.s3.eu-west-2.amazonaws.com/tiger.svg"
                      }
                    />
                    <Text
                      style={[
                        AppFontStyle.BOLD_ANYBODY_14,
                        { alignSelf: "center", marginBottom: 10 },
                      ]}
                    >
                      {details?.participantDetails?.[0]?.userID || "Player 1"}
                    </Text>
                  </TouchableOpacity>
                </View>
                <View style={{ justifyContent: "center" }}>
                  <Image source={AppImages.icVsLogo} style={styles.vsImage} />
                </View>
                <View>
                  <Text
                    style={[AppFontStyle.BOLD_ANYBODY_22, { marginTop: 10 }]}
                  >
                    Player 2
                  </Text>
                  <Text
                    style={[
                      AppFontStyle.BOLD_ANYBODY_12,
                      {
                        marginVertical: 7,
                        color: AppColors.purple_B155FF,
                      },
                    ]}
                  >
                    Challenger
                  </Text>
                  <TouchableOpacity
                    onPress={() => {
                      toast.info("Please sign up to view player profiles");
                    }}
                    style={styles.playerView}
                  >
                    <Avatar
                      variant="OnGoingPlayerDetail"
                      src={
                        details?.participantDetails?.[1]
                          ? details?.participantDetails?.[1]?.profilePicture
                          : AppImages.icEmptyOpponent
                      }
                    />
                    <Text
                      style={[
                        AppFontStyle.BOLD_ANYBODY_14,
                        { alignSelf: "center", marginBottom: 10 },
                      ]}
                    >
                      {details?.participantDetails?.[1]?.userID || "........."}
                    </Text>
                  </TouchableOpacity>
                </View>
              </View>
            )}
            <Text
              style={[
                AppFontStyle.BOLD_ANYBODY_16,
                {
                  marginTop: 20,
                },
              ]}
            >
              Signed up Viewers
            </Text>

            {showLoader ? (
              <View style={{ marginVertical: 20, marginLeft: 10 }}>
                <Skeletons
                  height={
                    deviceSize === DEVICE_SIZES.XS ||
                    deviceSize === DEVICE_SIZES.SM
                      ? 270
                      : 300
                  }
                  width={
                    deviceSize === DEVICE_SIZES.XS ||
                    deviceSize === DEVICE_SIZES.SM
                      ? "100%"
                      : "61%"
                  }
                  style={{
                    borderRadius: 10,
                  }}
                />
              </View>
            ) : (
              <View style={styles.userView}>
                <View style={{ height: 360 }}>
                  {mergedDetails.length > 0 ? (
                    <ScrollView
                      style={{ flex: 1 }}
                      contentContainerStyle={{
                        flexDirection: "row",
                        flexWrap: "wrap",
                        gap: 8,
                      }}
                      showsVerticalScrollIndicator={false}
                    >
                      {mergedDetails.map((item, index) => (
                        <TouchableOpacity
                          key={index}
                          style={{
                            alignItems: "center",
                            marginVertical: 10,
                            padding: 12,
                            gap: 10,
                          }}
                          onPress={() => {
                            toast.info("Please sign up to view user profiles");
                          }}
                        >
                          <View
                            style={{
                              backgroundColor: AppColors.lightPurple_604e74,
                              borderRadius: 50,
                              width: 45,
                              height: 45,
                              overflow: "hidden",
                            }}
                          >
                            <Avatar
                              src={
                                item.profilePicture ??
                                "https://squadify-avatar.s3.eu-west-2.amazonaws.com/tiger.svg"
                              }
                              variant="pastSmallPlayer"
                            />
                          </View>
                          <Text
                            style={[
                              AppFontStyle.REGULAR_ANYBODY_12,
                              {
                                color: AppColors.gray_C3C3C3,
                                textAlign: "center",
                                marginVertical: 5,
                              },
                            ]}
                          >
                            {item?.userID}
                          </Text>
                        </TouchableOpacity>
                      ))}
                    </ScrollView>
                  ) : (
                    <View
                      style={{
                        flex: 1,
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                      }}
                    >
                      <NoDataFound />
                    </View>
                  )}
                </View>
              </View>
            )}
          </View>

          {/* Chat Section - Exact same structure as GameShowScreen */}
          {deviceSize !== DEVICE_SIZES.XS && (
            <View style={[styles.eventView, {}]}>
              {showLoader ? (
                <View style={{ marginTop: 25 }}>
                  <Skeletons
                    height={50}
                    width={"50%"}
                    style={{
                      borderRadius: 10,
                    }}
                  />
                  <Skeletons
                    height={1}
                    style={{
                      borderRadius: 10,
                    }}
                  />
                  <Skeletons
                    height={557}
                    style={{
                      margin: 10,
                      borderRadius: 10,
                    }}
                  />
                </View>
              ) : (
                <StaticEventChatPreview
                  deviceSize={deviceSize}
                  details={details}
                  title="Event Chat"
                  onChatTap={() => {
                    toast.info("Please sign up to participate in chat");
                  }}
                  IsEventCanceled={IsEventCanceled}
                  reason={
                    details?.cancelMessage
                      ? details?.cancelMessage
                      : details?.reason
                  }
                />
              )}
            </View>
          )}
        </View>

        {/* GameShow SignUp Modal - Auto-open */}
        {openSignUp && details && (
          <GameShowSignUpModal
            eventDetails={details}
            onClose={() => setOpenSignUp(false)}
            referralCode={referralCode}
            directReferrerId={directReferrerId}
            originalURL={originalURL}
          />
        )}
      </ScrollView>
    </View>
  );
}

// Use the exact same styles as GameShowScreen
const useStyles = CreateResponsiveStyle(
  {
    container: {
      flex: 1,
      backgroundColor: "transparent",
      paddingHorizontal: "2.5%",
      paddingTop: "2%",
    },
    headerSection: {
      marginBottom: 30,
    },
    titleContainer: {
      marginTop: 20,
      alignItems: "center",
    },
    countdownContainer: {
      backgroundColor: "transparent",
      borderRadius: 20,
      padding: 20,
      marginBottom: 20,
    },
    eventView: {
      marginTop: 5,
      alignSelf: "flex-start",
      flex: 0.5,
    },
    vsImage: {
      height: 80,
      width: 100,
      marginVertical: 10,
      alignSelf: "center",
    },
    playerImg: {
      width: 91,
      height: 95,
      marginVertical: 10,
    },
    timeContainer: {
      flexDirection: "row",
      columnGap: 50,
      justifyContent: "center",
      marginBottom: 20,
    },
    eventDetailsContainer: {
      alignSelf: "flex-end",
      marginBottom: 10,
      paddingVertical: 20,
    },
    userView: {
      backgroundColor: AppColors.grayColor_282828,
      borderRadius: 10,
      marginVertical: 20,
      width: "75%",
    },
    gameshowOuterView: {
      margin: 5,
      backgroundColor: AppColors.grayColor_282828,
      borderRadius: 12,
      paddingHorizontal: 10,
    },
    chatOuterView: {
      margin: 5,
      backgroundColor: AppColors.grayColor_282828,
      borderRadius: 12,
      paddingHorizontal: 10,
      marginVertical: 10,
    },

    playersView: {
      width: "75%",
      flexDirection: "row",
      gap: 10,
    },
    playerView: {
      margin: 5,
      backgroundColor: AppColors.grayColor_282828,
      borderRadius: 12,
      paddingHorizontal: 10,
      alignItems: "center",
    },
    eventDetils: {
      flexDirection: "row",
      gap: 10,
      marginTop: 25,
    },
    btnSignUpStyle: {
      paddingVertical: 0,
      paddingHorizontal: 15,
      backgroundColor: AppColors.appGreenColor_51AF07,
      shadowOpacity: 0,
      height: 40,
      borderRadius: 7,
    },
    btnEventChatStyle: {
      paddingVertical: 8,
      paddingHorizontal: 16,
      backgroundColor: "transparent",
      borderWidth: 1.5,
      borderColor: "#DC2626",
      shadowOpacity: 0,
      height: "auto",
      borderRadius: 50,
      gap: 10,
      flexDirection: "row",
      justifyContent: "center",
      alignItems: "center",
    },
    btnEventChatText: {
      color: "#DC2626",
      fontSize: 14,
      fontWeight: "600",
      lineHeight: 18,
    },
    btnButTicket: {
      paddingVertical: 0,
      paddingHorizontal: 55,
      backgroundColor: AppColors.appGreenColor_51AF07,
      shadowOpacity: 0,
      height: 40,
      borderRadius: 7,
    },
    btnChat: {
      paddingVertical: 0,
      paddingHorizontal: 15,
      backgroundColor: AppColors.appViolentColor_6D08C3,
      shadowOpacity: 0,
      height: 40,
      marginTop: 10,
      borderRadius: 7,
    },
    freeEventView: {
      flexDirection: "row",
      columnGap: 30,
    },
    deviceflexDirection: {
      flexDirection: "row",
    },
  },
  {
    [DEVICE_SIZES.MD]: {
      container: {
        flex: 1,
        backgroundColor: "transparent",
        paddingHorizontal: "2.5%",
        paddingTop: "2%",
      },
      countdownContainer: {
        backgroundColor: "transparent",
        borderRadius: 20,
        padding: 20,
        marginBottom: 20,
      },

      btnButTicket: {
        paddingVertical: 0,
        paddingHorizontal: 45,
        backgroundColor: AppColors.appGreenColor_51AF07,
        shadowOpacity: 0,
        height: 40,
        borderRadius: 7,
      },
      freeEventView: {
        flexDirection: "row",
        columnGap: 5,
      },
      timeContainer: {
        flexDirection: "row",
        columnGap: 50,
        justifyContent: "center",
        marginBottom: 20,
      },
      userView: {
        backgroundColor: AppColors.grayColor_282828,
        borderRadius: 10,
        marginVertical: 20,
        width: "90%",
      },
      eventDetailsContainer: {
        alignSelf: "center",
        flex: 1,
        marginBottom: 10,
        paddingVertical: 20,
      },
      gameshowOuterView: {
        margin: 5,
        backgroundColor: AppColors.grayColor_282828,
        borderRadius: 12,
        paddingHorizontal: 10,
      },

      eventView: {
        marginTop: 5,
        alignSelf: "flex-start",
        flex: 0.9,
        width: "100%",
      },
      eventDetils: {
        flexDirection: "row",
        columnGap: 17,
        marginTop: 25,
      },
      btnSignUpStyle: {
        paddingVertical: 0,
        paddingHorizontal: 15,
        backgroundColor: AppColors.appGreenColor_51AF07,
        shadowOpacity: 0,
        height: 40,
        borderRadius: 7,
      },
      btnChat: {
        paddingVertical: 0,
        paddingHorizontal: 15,
        backgroundColor: AppColors.appViolentColor_6D08C3,
        shadowOpacity: 0,
        height: 40,
        marginTop: 10,
        borderRadius: 7,
      },
      deviceflexDirection: {
        flexDirection: "column",
      },
      chatOuterView: {
        margin: 5,
        backgroundColor: AppColors.grayColor_282828,
        borderRadius: 12,
        paddingHorizontal: 10,
        marginVertical: 10,
      },
    },
    [DEVICE_SIZES.LG]: {
      container: {
        flex: 1,
        backgroundColor: "transparent",
        paddingHorizontal: "2.5%",
        paddingTop: "2%",
      },
      countdownContainer: {
        backgroundColor: "transparent",
        borderRadius: 20,
        padding: 20,
        marginBottom: 20,
      },

      btnButTicket: {
        paddingVertical: 0,
        paddingHorizontal: 55,
        backgroundColor: AppColors.appGreenColor_51AF07,
        shadowOpacity: 0,
        height: 40,
        borderRadius: 7,
      },
      freeEventView: {
        flexDirection: "row",
        columnGap: 10,
      },

      userView: {
        backgroundColor: AppColors.grayColor_282828,
        borderRadius: 10,
        marginVertical: 20,
        marginRight: 400,
        width: "85%",
      },
      eventView: {
        marginTop: 5,
        alignSelf: "flex-start",
        flex: 0.8,
      },
      timeContainer: {
        flexDirection: "row",
        columnGap: 50,
        justifyContent: "center",
        marginBottom: 20,
      },
      eventDetailsContainer: {
        alignSelf: "center",
        flex: 1,
        marginBottom: 10,
        paddingVertical: 20,
      },
      gameshowOuterView: {
        margin: 5,
        backgroundColor: AppColors.grayColor_282828,
        borderRadius: 12,
        paddingHorizontal: 10,
      },
      eventDetils: {
        flexDirection: "row",
        columnGap: 20,
        marginTop: 25,
      },
      btnSignUpStyle: {
        paddingVertical: 0,
        paddingHorizontal: 15,
        backgroundColor: AppColors.appGreenColor_51AF07,
        shadowOpacity: 0,
        height: 40,
        borderRadius: 7,
      },
      btnChat: {
        paddingVertical: 0,
        paddingHorizontal: 15,
        backgroundColor: AppColors.appViolentColor_6D08C3,
        shadowOpacity: 0,
        height: 40,
        marginTop: 10,
        borderRadius: 7,
      },
      deviceflexDirection: {
        flexDirection: "column",
      },
      chatOuterView: {
        margin: 5,
        backgroundColor: AppColors.grayColor_282828,
        borderRadius: 12,
        paddingHorizontal: 10,
        marginVertical: 10,
      },
    },

    [DEVICE_SIZES.SM]: {
      container: {
        flex: 1,
        backgroundColor: "transparent",
        paddingHorizontal: "2.5%",
        paddingTop: "2%",
      },
      freeEventView: {
        flexDirection: "column",
        columnGap: 0,
      },
      btnButTicket: {
        paddingVertical: 0,
        paddingHorizontal: 25,
        backgroundColor: AppColors.appGreenColor_51AF07,
        shadowOpacity: 0,
        height: 40,
        borderRadius: 7,
        bottom: 10,
      },
      countdownContainer: {
        backgroundColor: "transparent",
        borderRadius: 20,
        padding: 20,
        marginBottom: 20,
      },
      playersView: {
        width: "100%",
        flexDirection: "row",
        gap: 0,
        justifyContent: "center",
        alignContent: "center",
      },

      eventView: {
        marginTop: 5,
        alignSelf: "flex-start",
        flex: 1,
        width: "100%",
      },
      timeContainer: {
        flexDirection: "row",
        columnGap: 50,
        justifyContent: "center",
        marginBottom: 20,
      },
      userView: {
        backgroundColor: AppColors.grayColor_282828,
        borderRadius: 10,
        marginVertical: 20,
        marginHorizontal: 0,
        width: "auto",
      },
      eventDetailsContainer: {
        borderRadius: 10,
        alignSelf: "flex-end",
        flex: 1,
        marginBottom: 10,
        paddingVertical: 20,
      },
      gameshowOuterView: {
        margin: 0,
        backgroundColor: AppColors.grayColor_282828,
        borderRadius: 12,
        paddingHorizontal: 10,
      },
      eventDetils: {
        flexDirection: "row",
        columnGap: 17,
        marginTop: 25,
      },
      btnSignUpStyle: {
        paddingVertical: 0,
        paddingHorizontal: 2,
        backgroundColor: AppColors.appGreenColor_51AF07,
        shadowOpacity: 0,
        height: 40,
        borderRadius: 7,
      },
      btnChat: {
        paddingVertical: 0,
        paddingHorizontal: 15,
        backgroundColor: AppColors.appViolentColor_6D08C3,
        shadowOpacity: 0,
        height: 40,
        marginTop: 10,
        borderRadius: 7,
      },
      deviceflexDirection: {
        flexDirection: "column",
      },
      chatOuterView: {
        margin: 5,
        backgroundColor: AppColors.grayColor_282828,
        borderRadius: 12,
        paddingHorizontal: 10,
      },
    },
    [DEVICE_SIZES.XS]: {
      container: {
        flex: 1,
        backgroundColor: "transparent",
        paddingHorizontal: "2.5%",
        paddingTop: "2%",
      },
      countdownContainer: {
        backgroundColor: "transparent",
        borderRadius: 20,
        padding: 20,
        marginBottom: 20,
      },
      btnButTicket: {
        paddingVertical: 0,
        paddingHorizontal: 35,
        backgroundColor: AppColors.appGreenColor_51AF07,
        shadowOpacity: 0,
        height: 40,
        borderRadius: 7,
        bottom: 0,
      },
      freeEventView: {
        flexDirection: "column",
        columnGap: 10,
      },
      playersView: {
        width: "100%",
        flexDirection: "row",
        gap: 10,
        justifyContent: "center",
        alignContent: "center",
      },
      eventView: {
        marginTop: 5,
        alignSelf: "flex-start",
        flex: 0.5,
        width: "100%",
      },

      userView: {
        backgroundColor: AppColors.grayColor_282828,
        borderRadius: 10,
        marginVertical: 20,
        marginHorizontal: 0,
        width: "auto",
      },
      timeContainer: {
        flexDirection: "row",
        columnGap: 20,
        justifyContent: "center",
        marginBottom: 20,
      },
      eventDetailsContainer: {
        alignSelf: "center",
        flex: 1,
        marginBottom: 10,
        paddingVertical: 20,
        marginHorizontal: 15,
      },
      gameshowOuterView: {
        margin: 0,
        backgroundColor: AppColors.grayColor_282828,
        borderRadius: 12,
        paddingHorizontal: 10,
      },
      eventDetils: {
        flexDirection: "column",
        gap: 17,
        marginTop: 25,
        columnGap: 20,
      },
      btnSignUpStyle: {
        paddingVertical: 0,
        paddingHorizontal: 15,
        backgroundColor: AppColors.appGreenColor_51AF07,
        shadowOpacity: 0,
        height: 40,
        borderRadius: 7,
      },
      btnChat: {
        paddingVertical: 0,
        paddingHorizontal: 15,
        backgroundColor: AppColors.appViolentColor_6D08C3,
        shadowOpacity: 0,
        height: 40,
        marginTop: 10,
        borderRadius: 7,
      },
      deviceflexDirection: {
        flexDirection: "column",
      },
      chatOuterView: {
        margin: 5,
        backgroundColor: AppColors.grayColor_282828,
        borderRadius: 12,
        paddingHorizontal: 10,
        marginVertical: 10,
      },
    },
  }
);
